import React, { useMemo } from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../MyAppText';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';
import { translate } from '../../../services/translate';

type Props = {
    label: string;
    maxValue?: number;
    minValue?: number;
    disabled?: boolean;
    placeholder?: string;
    required?: boolean;
} & ({
    form?: undefined;
    fieldName?: undefined;
    customValidations?: undefined;
    value: number | null;
    onChange: (value: number | null) => void;
} | {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any) => string | true;
    };
    value?: undefined;
    onChange?: undefined;
});

function FilterInputNumeric({ label, value, onChange, maxValue, minValue, placeholder, required, disabled, form, fieldName, customValidations }: Props) {
    const { styles, theme } = useStyles(stylesheet);

    placeholder = placeholder ?? label;

    const isPlaceholder = useMemo(() => !value && value === null, [value]);

    if (form && fieldName) {
        const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

        if (required) {
            rules.required = translate('required_field');
        }

        if (customValidations) {
            rules.validate = { ...customValidations };
        }

        return (
            <View style={styles.inputContainer}>
                <View style={{ flexDirection: 'row', gap: 3 }}>
                    <MyAppText style={styles.filterText}>
                        {label}
                    </MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
                <Controller
                    control={form.control}
                    name={fieldName}
                    rules={rules}
                    render={({ field, fieldState }) => (
                        <>
                            <input
                                style={styles.filterInput(isPlaceholder, disabled ?? false)}
                                value={field.value !== null ? field.value : ''}
                                onChange={(e) => {
                                    const parsedValue = e.target.value.trim() === '' ? null : parseFloat(e.target.value);
                                    if (parsedValue === null || !isNaN(parsedValue)) {
                                        field.onChange(parsedValue);
                                    }
                                }}
                                placeholder={placeholder}
                                type='number'
                                step='1'
                                max={maxValue}
                                min={minValue ?? 0}
                                disabled={disabled ?? false}
                            />
                            {fieldState.error ? <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText> : null}
                        </>
                    )}
                />
            </View >
        );
    } else if (value !== undefined && onChange !== undefined) {
        return (
            <View style={styles.inputContainer}>
                <View style={{ flexDirection: 'row', gap: 3 }}>
                    <MyAppText style={styles.filterText}>
                        {label}
                    </MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
                <input
                    style={styles.filterInput(isPlaceholder, disabled ?? false)}
                    value={value !== null ? value : ''}
                    onChange={(e) => {
                        const parsedValue = e.target.value.trim() === '' ? null : parseFloat(e.target.value);
                        if (parsedValue === null || !isNaN(parsedValue)) {
                            onChange(parsedValue);
                        }
                    }}
                    placeholder={placeholder}
                    type='number'
                    step='1'
                    max={maxValue}
                    min={minValue ?? 0}
                    disabled={disabled ?? false}
                />
            </View >
        );
    }
}

const stylesheet = createStyleSheet(theme => ({
    inputContainer: {
        minWidth: 200,
    },

    filterText: {
        color: theme.colors.labelColor,
        fontSize: 16,
        height: 25,
    },

    filterInput: (isPlaceholder: boolean, disabled: boolean) => ({
        fontFamily: 'Open Sans',
        backgroundColor: disabled ? theme.colors.field.disabledBackground : theme.colors.field.background,
        borderWidth: 1,
        borderRadius: 4,
        borderColor: theme.colors.field.border,
        height: 40,
        minHeight: 40,
        fontSize: 16,
        color: isPlaceholder ? theme.colors.placeholder : theme.colors.field.text,
        paddingLeft: 10,
        paddingRight: 10,
        paddingVertical: 3,
    }),

    error: {
        color: theme.colors.errors['300'],
        fontSize: 10
    },
}));

export default FilterInputNumeric;
